/* eslint-disable no-underscore-dangle */
import JsSIP from 'jssip';
import { markRaw } from 'vue';

class VoiceCallService {
  constructor() {
    this.ua = null;
    this.currentCallId = null;
    this.activeCalls = new Map();
    this.callTimers = new Map();
    this.remoteAudio = null;

    // Callbacks para o store
    this.onIncomingCall = null;
    this.onCallEnded = null;
    this.onCallFailed = null;
    this.onCallStarted = null;
    this.onCallConfirmed = null;
  }

  async initialize(userConfig) {
    const socket = new JsSIP.WebSocketInterface(
      `wss://${userConfig.server}/api/asterisk/ws`
    );
    socket.via_transport = 'tcp';

    const configuration = {
      sockets: [socket],
      uri: `sip:${userConfig.sip_extension}@${userConfig.app_url}:8089`,
      password: userConfig.sip_password,
      display_name:
        userConfig.displayName || `Agente ${userConfig.sip_extension}`,
      session_timers: false,
    };
    this.ua = new JsSIP.UA(configuration);
    this.setupEvents();
    this.ua.start();
  }

  setupEvents() {
    this.ua.on('newRTCSession', e => {
      const session = e.session;
      const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      this.currentCallId = id;

      this.activeCalls.set(id, session);

      const callData = {
        id,
        session: markRaw(session),
        direction: e.originator === 'remote' ? 'incoming' : 'outgoing',
        startTime: new Date(),
        status: 'ringing',
        from: session._request?.from?.uri?._user || '',
        to: session._request?.to?.uri?._user || '',
      };

      // Notifica o store
      if (e.originator === 'remote') {
        this.onIncomingCall?.(callData);
      } else {
        this.onCallStarted?.(callData);
      }

      // Configura eventos da sessão
      session.on('confirmed', () => {
        this.clearTimer();
        this.setupAudioEvents(session);
        this.onCallConfirmed();
      });

      session.on('ended', () => {
        this.clearTimer();
        this.cleanupAudioElements();
        this.onCallEnded?.(id);
        this.activeCalls.delete(id);
      });

      session.on('failed', () => {
        this.clearTimer();
        this.cleanupAudioElements();
        this.onCallFailed?.(id);
        this.activeCalls.delete(id);
      });

      session.on('progress', () => {
        if (this.callTimers.has(id)) return;

        const timer = setTimeout(
          () => {
            this.cleanupAudioElements();
            this.onCallEnded?.(id);
            this.activeCalls.delete(id);
            if (!session.isEnded()) {
              session.terminate();
            }
          },
          import.meta.env.VITE_CALL_TIMEOUT || 30000
        );
        this.callTimers.set(id, timer);
      });

      session.on('accepted', () => {
        this.clearTimer();
      });
    });
  }

  clearTimer() {
    const t = this.callTimers.get(this.currentCallId);
    if (t) {
      clearTimeout(t);
      this.callTimers.delete(this.currentCallId);
    }
  }

  setupAudioEvents(session) {
    if (!session || !session.connection) {
      return;
    }

    try {
      // Para navegadores modernos (WebRTC)
      if (session.connection.getReceivers) {
        session.connection.addEventListener('track', event => {
          if (event.track.kind === 'audio' && event.streams.length > 0) {
            this.setupAudioElements(event.streams[0]);
          }
        });
      }

      // Para navegadores antigos
      session.connection.addEventListener('addstream', event => {
        this.setupAudioElements(event.stream);
      });

      // Verifica streams existentes
      setTimeout(() => {
        if (session.connection.getRemoteStreams) {
          const remoteStreams = session.connection.getRemoteStreams();
          if (remoteStreams.length > 0) {
            this.setupAudioElements(remoteStreams[0]);
          }
        }

        // Alternativa usando getReceivers
        if (session.connection.getReceivers) {
          session.connection.getReceivers();
        }
      }, 1000);
    } catch (error) {
      throw new Error(error);
    }
  }

  setupAudioElements(stream) {
    if (!stream) {
      return;
    }
    this.cleanupAudioElements();

    try {
      this.remoteAudio = document.createElement('audio');
      this.remoteAudio.id = 'remoteAudio';
      this.remoteAudio.autoplay = true;
      this.remoteAudio.controls = false;
      this.remoteAudio.style.display = 'none';

      // Usa srcObject para navegadores modernos
      this.remoteAudio.srcObject = stream;
      document.body.appendChild(this.remoteAudio);
      this.remoteAudio.addEventListener('loadedmetadata', () => {});
      this.remoteAudio.addEventListener('canplay', () => {});
      this.remoteAudio.addEventListener('error', error => {
        throw error;
      });
    } catch (error) {
      throw new Error(error);
    }
  }

  cleanupAudioElements() {
    if (this.remoteAudio) {
      try {
        this.remoteAudio.pause();
        this.remoteAudio.srcObject = null;
        if (this.remoteAudio.parentNode) {
          this.remoteAudio.parentNode.removeChild(this.remoteAudio);
        }
      } catch (error) {
        throw new Error(error);
      }
      this.remoteAudio = null;
    }
  }

  makeCall(number) {
    if (!this.ua) throw new Error('UA não inicializada');

    const options = {
      mediaConstraints: { audio: true, video: false },
      pcConfig: {
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
      },
    };

    const uriObj = this.ua.configuration.uri;
    const domain = uriObj._host + (uriObj._port ? ':' + uriObj._port : '');
    const targetUri = `sip:${number}@${domain}`;

    return this.ua.call(targetUri, options);
  }

  acceptCall(session) {
    session.answer({ mediaConstraints: { audio: true, video: false } });
    this.setupAudioEvents(session);
  }

  rejectCall(session) {
    this.cleanupAudioElements();
    session.terminate();
  }

  rejectBusy(session) {
    this.cleanupAudioElements();
    session.terminate({
      status_code: 486,
      reason_phrase: 'Busy Here',
    });
  }

  endCall(session) {
    if (!session) return;
    this.cleanupAudioElements();
    if (session.isEnded()) {
      return;
    }
    session.terminate();
  }
}

export default VoiceCallService;
